import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '@strava/ui/Button';
import { isEmpty, size } from 'lodash-es';
import Spinner from '@strava/ui/Spinner';
import styles from './GmailContactList.scss';

import { t } from '../../../../../../utils/invite-friends-utils';
import ContactEntry from './components/ContactEntry';
import { useInviteFriendsContext } from '../../../../../../state/InviteFriendsContext';
import { CLEAR_ALERT } from '../../../../../../state/InviteFriends.actions';

const GmailContactList = ({ contacts, disconnect, isLoading }) => {
  const [searchKeyword, setSearchKeyword] = useState(''); // value entered in the search field
  const [filterResults, setFilterResults] = useState(contacts);

  const { dispatch, track } = useInviteFriendsContext();

  // keeps track if its the initial search results (with no filter applied).
  // This is necessary because filtered results change based on the search box input
  // and we dont want to re-register a screen-enter at that point.
  const initialResults = useRef(true);

  useEffect(() => {
    if (!isLoading && initialResults.current) {
      track({
        element: 'google_contacts_feed',
        action: 'screen_enter',
        properties: {
          current_tab: 'email',
          contacts_count: filterResults.length,
          already_on_strava_count: size(
            filterResults.filter((contact) => contact.isOnStrava)
          )
        }
      });
      initialResults.current = false;
    }
  }, [isLoading, filterResults, track]);

  useEffect(() => {
    setFilterResults(contacts);
  }, [contacts]);

  const filterContacts = (e) => {
    dispatch({
      type: CLEAR_ALERT
    });

    const keyword = e.target.value;
    if (keyword !== '') {
      const results = contacts.filter((contact) => {
        return (
          contact.name.toLowerCase().includes(keyword.toLowerCase()) ||
          contact.email.toLowerCase().includes(keyword.toLowerCase())
        );
      });
      setFilterResults(results);
    } else {
      setFilterResults(contacts); // show all contacts
    }
    setSearchKeyword(keyword);
  };

  const handleOnDisconnect = () => {
    track({
      element: 'disconnect_gmail_contacts',
      action: 'click',
      properties: { current_tab: 'email' }
    });
    dispatch({ type: CLEAR_ALERT });
    disconnect();
  };

  return (
    <>
      <h3 className={styles.headline}>
        {t('modal.email.gmail_authorized.title')}
      </h3>
      <Button
        className={styles.disconnectBtn}
        variant="text"
        onClick={handleOnDisconnect}
        type="button"
      >
        {t('modal.email.gmail_authorized.disconnect')}
      </Button>

      <input
        type="search"
        value={searchKeyword}
        onChange={filterContacts}
        className={styles.searchInput}
        placeholder={t('modal.email.gmail_authorized.search_label')}
        disabled={isEmpty(contacts)}
        aria-label={t('modal.email.gmail_authorized.search_aria_label')}
      />

      <ResultsFeed isLoading={isLoading} results={filterResults} />
    </>
  );
};

const ResultsFeed = ({ isLoading, results }) => {
  if (isLoading) {
    return (
      <div className={styles.spinner}>
        <Spinner />
      </div>
    );
  }
  if (results && results.length > 0) {
    return (
      <ul className={styles.contactList}>
        {results.map((contact) => (
          <li key={contact.key}>
            <ContactEntry contact={contact} />
          </li>
        ))}
      </ul>
    );
  }
  return (
    <p className={styles.noResults}>
      {t('modal.email.gmail_authorized.no_results_found')}
    </p>
  );
};

ResultsFeed.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  results: PropTypes.arrayOf(PropTypes.shape({})).isRequired
};

GmailContactList.propTypes = {
  contacts: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  disconnect: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired
};

export default GmailContactList;
